import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DOCUMENT, enableProdMode, ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication, provideProtractorTestingSupport } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideServiceWorker, SwRegistrationOptions } from '@angular/service-worker';
import { THEME_CONFIG, ThemeService, ThemeServiceConfig } from '@bcodes/ngx-theme-service';
import { FORMLY_CONFIG, provideFormlyCore } from '@ngx-formly/core';
import { provideTranslateCompiler, provideTranslateService, TranslateLoader, TranslateParser, TranslateService } from '@ngx-translate/core';
import { provideApollo } from 'apollo-angular';
import { ClipboardService } from 'ngx-clipboard';
import { NG_SCROLLBAR_OPTIONS, NgScrollbarModule } from 'ngx-scrollbar';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { NgxUiLoaderHttpModule, NgxUiLoaderModule } from 'ngx-ui-loader';
import { LocalStorageService, provideNgxWebstorage, withLocalStorage, withSessionStorage } from 'ngx-webstorage';
import { MessageService } from 'primeng/api';
import { PrimeNG, providePrimeNG } from 'primeng/config';
import { BusyInterceptor } from './app/@core/interceptors/busy.interceptor';
import { DefaultInterceptor } from './app/@core/interceptors/default.interceptor';
import { MaintenanceInterceptor } from './app/@core/interceptors/maintenance.interceptor';
import { BusyService } from './app/@core/services/busy.service';
import { GlobalErrorHandler } from './app/@core/logging/global-error-handler.component';
import { CssLoader } from './app/@core/services/css-loader.service';
import { CachedCustomHttpTranslationLoader } from './app/@core/services/i18n/custom-http-loader';
import { I18nService } from './app/@core/services/i18n/i18n.service';
import { initUiLanguagesInitializer } from './app/@core/services/i18n/ui-language.initializer';
import { SettingsService } from './app/@core/services/settings.service';
import { isServiceWorkerRegistrationEnabled } from './app/@core/utils/service-worker.util';
import { AppComponent } from './app/app.component';
import { hotKeyOptions, loadAppConfig, MyPreset, ngxUiLoaderConfig, PhoenixUiConfigLoadError, swissTranslateParserFactory, themeServiceConfig, WINDOW, WINDOW_PROVIDERS } from './app/app.config';
import { routes } from './app/app.routes';
import { betaFeatureExtension } from './app/beta-feature-formly';
import { fillHeightExtension } from './app/modules/custom-formly/fill-height-formly.extension';
import { layoutEditExtension } from './app/modules/custom-formly/layout-edit-formly.extension';
import { createApollo } from './app/data/apollo.settings';
import { customCssLoaderInitializer, forceSSLProtocolInitializer, initializeServerConfigService, ServerConfigService } from './app/data/server-config';
import { FetchAdapter } from './app/data/services/fetch-adapter';
import { customFormlyConfig } from './app/modules/custom-formly/custom-formly.config';
import { HotkeyOptionsToken } from './app/modules/hotkeys/types-interfaces/hotkey.options';
import { COLLECTIVE_DOCUMENT_DIALOG_PROVIDERS } from './app/modules/documents/providers/collective-document-dialog.provider';
import { CUSTOM_ACTION_BAR_REPORT_LAUNCHER_PROVIDERS } from './app/modules/printing/providers/custom-action-bar-report-launcher.provider';
import { REPORT_TRIGGER_EXECUTION_PROVIDERS } from './app/modules/printing/providers/report-trigger-execution.provider';
import { LocaleDatePipe } from './app/shared/pipes/locale-date.pipe';
import { registerTranslateExtension } from './app/translate.extension';
import { environment } from './environments/environment';

/** Escape text for safe insertion into HTML (bootstrap failure UI only). */
function escapeHtmlForBootstrapError(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Plain-text blob for support: message, JSON diagnostics, stack (for pre + clipboard). */
function formatBootstrapLoadError(error: unknown): string {
  if (error instanceof PhoenixUiConfigLoadError) {
    const supportPayload = {
      ...error.diagnostics,
      logHint:
        'Correlate with server access logs using header X-PHX-Config-Request-Id or field requestId below.',
    };
    const parts = [
      error.message,
      '',
      '--- Diagnostics (copy and send to support) ---',
      JSON.stringify(supportPayload, null, 2),
      '',
      '--- Stack ---',
      error.stack ?? '',
    ];
    return parts.join('\n');
  }
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

// Performance optimization: Use faster bootstrap process
/** NgModule bootstrap (not importProvidersFrom + bootstrapApplication) so DI factories like Apollo resolve in a proper injection context (avoids NG0203). */
const bootstrap = async () => {
  console.time('Bootstrap Time');
  const app = await bootstrapApplication(AppComponent, {
    providers: [
      // provideZonelessChangeDetection(), // As i was testing, we are closer to move to zoneless change detection, but we need to test it more and see if it works for all cases.
      provideZoneChangeDetection({ eventCoalescing: true }),
      // Same URL must still navigate. Otherwise a second visit cannot clear one-shot `ssr` after it was removed.
      provideRouter(routes, withRouterConfig({ onSameUrlNavigation: 'reload' })),
      provideAnimationsAsync(),
      provideHttpClient(withInterceptorsFromDi()),
      ...(environment.production ? [] : [provideProtractorTestingSupport()]),
      providePrimeNG({
        ripple: true,
        theme: {
          preset: MyPreset,
          options: {
            prefix: 'p',
            darkModeSelector: '.dark'
          }
        }
      }),
      provideApollo(() => createApollo(
        inject(LocalStorageService),
        inject(FetchAdapter)
      )),
      importProvidersFrom(
        NgxUiLoaderModule.forRoot(ngxUiLoaderConfig), // Not sure if its necessary like this here it might be a provider for it.
        NgxUiLoaderHttpModule, // Not sure if its necessary here it might be a provider for it.
        NgScrollbarModule, // Not sure if its necessary here it might be a provider for it.
      ),
      { provide: HotkeyOptionsToken, useValue: hotKeyOptions },
      // https://ngx-translate.org/getting-started/migration-guide/
      provideTranslateService({
        loader: {
          provide: TranslateLoader,
          useFactory: () => {
            const http = inject(HttpClient);
            const localStorageService = inject(LocalStorageService);
            const location = inject(PlatformLocation);
            // Dynamically get the baseHref, which is configured in the angular.json file
            let baseHref = location.getBaseHrefFromDOM();
            if (baseHref == "/")
              baseHref = "";
            return new CachedCustomHttpTranslationLoader(
              http,
              localStorageService,
              baseHref + '/i18n-messages/',
              '.json',
              10 * 60 * 1000, // 10 minutes cache TTL
              false // Enable logging
            );
          }
        },
        compiler: provideTranslateCompiler(TranslateMessageFormatCompiler),
        parser: {
          provide: TranslateParser,
          useFactory: swissTranslateParserFactory,
          deps: [LocalStorageService]
        }
      }),
      provideServiceWorker('ngsw-worker.js', {
        enabled: isServiceWorkerRegistrationEnabled(environment.production),
        // Register the ServiceWorker as soon as the app is stable
        // or after 30 seconds (whichever comes first).
        registrationStrategy: 'registerWhenStable:30000'
      }),
      provideFormlyCore(customFormlyConfig),
      provideNgxWebstorage(
        withLocalStorage(),
        withSessionStorage()
      ),
      {
        provide: NG_SCROLLBAR_OPTIONS,
        useValue: {
          //if we dont use it like this, it will fetch from cdn and it will fail in some environments (also not secure)
          scrollTimelinePolyfill: '/assets/scroll-timeline-lite.js'
        }
      },
      { provide: APP_BASE_HREF, useValue: '/' },
      {
        provide: SwRegistrationOptions,
        useFactory: () => ({ enabled: isServiceWorkerRegistrationEnabled(environment.production) }),
      },
      {
        provide: THEME_CONFIG,
        useValue: themeServiceConfig
      },
      { provide: DOCUMENT, useValue: document },
      {
        provide: ThemeService,
        useFactory: (config: ThemeServiceConfig, document: Document) => new ThemeService(config, document),
        deps: [THEME_CONFIG, DOCUMENT]
      },
      { provide: FORMLY_CONFIG, multi: true, useFactory: registerTranslateExtension, deps: [TranslateService] },
      { provide: FORMLY_CONFIG, multi: true, useFactory: betaFeatureExtension, deps: [SettingsService] },
      { provide: FORMLY_CONFIG, multi: true, useFactory: fillHeightExtension },
      { provide: FORMLY_CONFIG, multi: true, useFactory: layoutEditExtension },
      {
        provide: ErrorHandler,
        useClass: GlobalErrorHandler
      },
      // e2e builds only: <html data-busy> for the tests (BusyService). Created at startup so the flag exists before
      // the first test step; the interceptor comes first so it wraps the others.
      ...(environment.e2e
        ? [{ provide: HTTP_INTERCEPTORS, useClass: BusyInterceptor, multi: true }, provideAppInitializer(() => { inject(BusyService); })]
        : []),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: MaintenanceInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: DefaultInterceptor,
        multi: true
      },
      provideAppInitializer(() => forceSSLProtocolInitializer(inject(PlatformLocation), inject(WINDOW))()), provideAppInitializer(() => customCssLoaderInitializer(inject(CssLoader))()),
      provideAppInitializer(() => initUiLanguagesInitializer(inject(PrimeNG), inject(TranslateService), inject(I18nService), inject(LocalStorageService))()),
      provideAppInitializer(() => initializeServerConfigService(inject(ServerConfigService))()),
      WINDOW_PROVIDERS,
      ...REPORT_TRIGGER_EXECUTION_PROVIDERS,
      ...CUSTOM_ACTION_BAR_REPORT_LAUNCHER_PROVIDERS,
      ...COLLECTIVE_DOCUMENT_DIALOG_PROVIDERS,
      ClipboardService, // Check if it really need to be globally provided
      MessageService, // Check if it really need to be globally provided
      LocaleDatePipe
    ]
  });
  console.timeEnd('Bootstrap Time');
  return app;
};

const loadAppConfigAndBootstrap = async () => {
  try {
    // Load config and bootstrap in parallel where possible
    console.time('Config Load Time');
    await loadAppConfig();
    console.timeEnd('Config Load Time');

    await bootstrap();
  } catch (error: unknown) {
    console.error('loadAppConfigAndBootstrap', error);
    const plainDetail = formatBootstrapLoadError(error);
    const errorDetail = escapeHtmlForBootstrapError(plainDetail);
    const configLoadError = error instanceof PhoenixUiConfigLoadError ? error : null;
    const detailsVisible = configLoadError ? 'block' : 'none';
    const toggleLabel = configLoadError ? 'Hide error details' : 'Show error details';
    const codeStyle =
      'display: block; margin: 2px 0 0; padding: 0; font-size: 12px; word-break: break-all; white-space: pre-wrap; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;';
    const rowStyle = 'text-align: left; margin: 0 0 6px;';
    const rowLastStyle = 'text-align: left; margin: 0;';
    const httpBit =
      configLoadError != null && typeof configLoadError.diagnostics.httpStatus === 'number'
        ? ` · <strong>HTTP</strong> ${configLoadError.diagnostics.httpStatus} ${escapeHtmlForBootstrapError(configLoadError.diagnostics.httpStatusText ?? '')}`
        : '';
    const summaryHtml = configLoadError
      ? `<div style="text-align: left; margin: 0 0 10px; padding: 8px 10px; background: #e8f4fc; border: 1px solid #b8d4e8; border-radius: 4px; font-size: 13px; line-height: 1.35;">
              <div style="${rowStyle}"><strong>Request ID</strong> <code style="${codeStyle}">${escapeHtmlForBootstrapError(configLoadError.diagnostics.requestId)}</code></div>
              <div style="${rowStyle}"><strong>Config URL</strong> <code style="${codeStyle}">${escapeHtmlForBootstrapError(configLoadError.diagnostics.configUrl)}</code></div>
              <div style="${rowStyle}"><strong>Duration</strong> ${configLoadError.diagnostics.durationMs} ms${httpBit} · <strong>Online</strong> ${configLoadError.diagnostics.navigatorOnLine ? 'yes' : 'no'}</div>
              <div style="${rowLastStyle}"><strong>Page</strong> <code style="${codeStyle}">${escapeHtmlForBootstrapError(configLoadError.diagnostics.pageUrl)}</code></div>
            </div>`
      : '';
    // Show user-friendly error message (pre-Angular; inline styles only)
    document.body.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; padding: 16px; box-sizing: border-box;">
        <div style="text-align: center; max-width: min(920px, 100%); width: 100%;">
          <h2 style="margin: 0 0 6px; font-size: 1.35rem;">Loading Error</h2>
          <p style="margin: 0 0 10px; font-size: 14px;">Failed to load the application. Please refresh the page.</p>
          ${summaryHtml}
          <div style="display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin: 0 0 8px;">
            <button type="button" onclick="window.location.reload()" style="padding: 10px 16px; min-height: 44px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 15px;">
              Refresh Page
            </button>
            <button type="button" id="phx-load-error-copy" style="padding: 10px 16px; min-height: 44px; background: #198754; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 15px;">
              Copy diagnostics
            </button>
            <button type="button" id="phx-load-error-toggle" style="padding: 10px 16px; min-height: 44px; background: #fff; color: #333; border: 1px solid #ccc; border-radius: 4px; cursor: pointer; font-size: 15px;">
              ${toggleLabel}
            </button>
          </div>
          <p id="phx-load-error-copy-status" style="display: none; margin: 0 0 6px; font-size: 13px; color: #198754;">Copied to clipboard.</p>
          <pre id="phx-load-error-detail" style="display: ${detailsVisible}; text-align: left; white-space: pre-wrap; word-break: break-word; margin: 0; padding: 10px 12px; background: #f5f5f5; border: 1px solid #ddd; border-radius: 4px; font-size: 12px; line-height: 1.4; max-height: min(65vh, 480px); overflow: auto; -webkit-overflow-scrolling: touch;">${errorDetail}</pre>
        </div>
      </div>
    `;
    const detailEl = document.getElementById('phx-load-error-detail');
    const toggleEl = document.getElementById('phx-load-error-toggle');
    const copyEl = document.getElementById('phx-load-error-copy');
    const copyStatusEl = document.getElementById('phx-load-error-copy-status');

    const setCopyStatusVisible = (visible: boolean) => {
      if (copyStatusEl) {
        copyStatusEl.style.display = visible ? 'block' : 'none';
      }
    };

    copyEl?.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(plainDetail);
        setCopyStatusVisible(true);
        window.setTimeout(() => setCopyStatusVisible(false), 3500);
      } catch {
        setCopyStatusVisible(false);
        if (detailEl) {
          detailEl.style.display = 'block';
        }
        if (toggleEl) {
          toggleEl.textContent = 'Hide error details';
        }
        window.alert('Could not copy automatically. Select the text in the details box below, then copy.');
      }
    });

    toggleEl?.addEventListener('click', () => {
      if (!detailEl || !toggleEl) {
        return;
      }
      const hidden = detailEl.style.display === 'none' || detailEl.style.display === '';
      detailEl.style.display = hidden ? 'block' : 'none';
      toggleEl.textContent = hidden ? 'Hide error details' : 'Show error details';
    });
  }
};

if (environment.production) {
  enableProdMode();
}

// Remove HMR checks for production optimization
loadAppConfigAndBootstrap();

